/**
 * Live perp MARK price for one perp (#786 touch markets: the touch rule measures a TWAP of the perp mark, not the mid).
 *
 * Source (#1809): the `activeAssetCtx` WS stream, one subscription per perp on the shared socket. Measured on mainnet and
 * testnet (BTC, xyz:CL, delisted perps): the first frame comes with the subscribe ack, then about 1 frame/s even when
 * nothing changes, and `ctx.markPx` is the same field and value as `markPx` in `metaAndAssetCtxs`. Frames are
 * {coin, ctx: {markPx, …}}; builder-dex perps keep their prefix ("xyz:CL"). A coin HL does not know gets an `error`
 * frame ("Invalid subscription") and no data.
 *
 * Fallback: REST `/info {"type":"metaAndAssetCtxs", "dex": <builder dex or "">}` every PERP_MARK_POLL_MS, only while the
 * stream is silent (no frame within PERP_MARK_FIRST_FRAME_MS of subscribing, or none for PERP_MARK_STALE_MS since the last
 * one). It stops on the next valid frame. Response: [meta {universe: [{name}]}, assetCtxs [{markPx, …}]] (same index);
 * builder-dex perps are named with their prefix in that dex's universe. Like the old TanStack poll it pauses in a hidden
 * tab. No REST seed: the subscribe snapshot is quicker than a REST call.
 *
 * A PerpMarkFeed exists once per socket + perp, so all users share its stream, timer and fallback. A zero, empty or
 * malformed mark is never used and does not count as a live frame, and a mark neither source has confirmed for
 * PERP_MARK_MAX_AGE_MS is dropped, so callers get undefined rather than a stale price.
 */
import { useMemo, useSyncExternalStore } from 'react';
import type { InfoClient } from './info';
import { useInfoClient, useWs } from './react';
import type { ActiveAssetCtxMsg } from './types';
import type { WsManager } from './ws';

/** REST fallback cadence. */
export const PERP_MARK_POLL_MS = 5_000;
/** How long a new subscription may stay silent before the fallback starts. */
export const PERP_MARK_FIRST_FRAME_MS = 2_500;
/** Frames are about 1/s: this long without one means the stream is not delivering. */
export const PERP_MARK_STALE_MS = 5_000;
/** A mark older than this is dropped, whatever the source. */
export const PERP_MARK_MAX_AGE_MS = 15_000;
const TICK_MS = 500;

export type MetaAndAssetCtxs = [{ universe?: { name?: string }[] }, { markPx?: string | null }[]];

/** `px` when it is a usable mark (a positive, finite decimal string), else undefined. */
function usableMark(px: unknown): string | undefined {
  if (typeof px !== 'string' || px === '') return undefined;
  const n = Number(px);
  return Number.isFinite(n) && n > 0 ? px : undefined;
}

/** markPx of `perp` in a metaAndAssetCtxs response, or undefined. */
export function markFrom(resp: unknown, perp: string): string | undefined {
  if (!Array.isArray(resp) || resp.length < 2) return undefined;
  const [meta, ctxs] = resp as MetaAndAssetCtxs;
  const i = meta?.universe?.findIndex((u) => u?.name === perp) ?? -1;
  if (i < 0 || !Array.isArray(ctxs)) return undefined;
  return usableMark(ctxs[i]?.markPx);
}

/** Builder dex of a perp name: "xyz:CL" → "xyz", "BTC" → "". */
export function dexOf(perp: string): string {
  const i = perp.lastIndexOf(':');
  return i >= 0 ? perp.slice(0, i) : '';
}

export interface PerpMark {
  mark: string | undefined;
  isLoading: boolean;
  error: unknown;
}

const IDLE: PerpMark = { mark: undefined, isLoading: false, error: null };
const LOADING: PerpMark = { mark: undefined, isLoading: true, error: null };

function tabHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

export class PerpMarkFeed {
  private readonly users = new Set<() => void>();
  private snapshot: PerpMark = LOADING;
  private release: (() => void) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private poll: AbortController | null = null;
  private startedAt = 0;
  private lastFrameAt: number | null = null;
  private lastPollAt: number | null = null;
  private markAt = 0;

  constructor(
    private readonly ws: WsManager,
    private readonly info: InfoClient,
    readonly perp: string,
  ) {}

  /** The first user starts the stream and the last one stops it. */
  subscribe = (cb: () => void): (() => void) => {
    this.users.add(cb);
    if (this.users.size === 1) this.start();
    return () => {
      this.users.delete(cb);
      if (this.users.size === 0) this.stop();
    };
  };

  getSnapshot = (): PerpMark => this.snapshot;

  private start(): void {
    this.startedAt = Date.now();
    this.release = this.ws.subscribe({ type: 'activeAssetCtx', coin: this.perp }, (data) => this.onFrame(data));
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  private stop(): void {
    this.release?.();
    this.release = null;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.cancelPoll();
    this.lastFrameAt = this.lastPollAt = null;
    this.snapshot = LOADING;
  }

  private onFrame(data: ActiveAssetCtxMsg): void {
    const px = usableMark(data?.ctx?.markPx);
    if (!px) return;
    this.lastFrameAt = Date.now();
    this.cancelPoll();
    this.accept(px);
  }

  private accept(mark: string): void {
    this.markAt = Date.now();
    this.publish({ mark, isLoading: false, error: null });
  }

  private publish(next: PerpMark): void {
    const cur = this.snapshot;
    if (cur.mark === next.mark && cur.isLoading === next.isLoading && cur.error === next.error) return;
    this.snapshot = next;
    for (const cb of [...this.users]) cb();
  }

  private streamLive(now: number): boolean {
    // A sleeping tab's socket is reopening: the first frame comes with the subscribe, as at the start.
    if (now - this.ws.resumedAt < PERP_MARK_FIRST_FRAME_MS) return true;
    return this.lastFrameAt === null
      ? now - this.startedAt < PERP_MARK_FIRST_FRAME_MS
      : now - this.lastFrameAt < PERP_MARK_STALE_MS;
  }

  private tick(): void {
    const now = Date.now();
    if (this.snapshot.mark !== undefined && now - this.markAt >= PERP_MARK_MAX_AGE_MS) {
      this.publish({ ...this.snapshot, mark: undefined });
    }
    if (this.streamLive(now) || this.poll || tabHidden()) return;
    if (this.lastPollAt === null || now - this.lastPollAt >= PERP_MARK_POLL_MS) this.startPoll(now);
  }

  private startPoll(now: number): void {
    const ctrl = new AbortController();
    this.poll = ctrl;
    this.lastPollAt = now;
    const dex = dexOf(this.perp);
    this.info.request<unknown>({ type: 'metaAndAssetCtxs', ...(dex ? { dex } : {}) }, { signal: ctrl.signal }).then(
      (resp) => {
        if (this.poll !== ctrl) return;
        this.poll = null;
        const px = markFrom(resp, this.perp);
        if (px) this.accept(px);
        else this.publish({ ...this.snapshot, isLoading: false, error: null });
      },
      (error: unknown) => {
        if (this.poll !== ctrl) return;
        this.poll = null;
        this.publish({ ...this.snapshot, isLoading: false, error });
      },
    );
  }

  private cancelPoll(): void {
    this.poll?.abort();
    this.poll = null;
  }
}

const feeds = new WeakMap<WsManager, Map<string, PerpMarkFeed>>();

/** The feed of `perp` on this socket. Feeds are kept with their socket; one without users holds no stream or timer. */
export function perpMarkFeed(ws: WsManager, info: InfoClient, perp: string): PerpMarkFeed {
  let byPerp = feeds.get(ws);
  if (!byPerp) feeds.set(ws, (byPerp = new Map()));
  let feed = byPerp.get(perp);
  if (!feed) byPerp.set(perp, (feed = new PerpMarkFeed(ws, info, perp)));
  return feed;
}

const noSubscribe = () => () => {};
const idleSnapshot = () => IDLE;

/** Current mark of `perp` (decimal string), kept fresh by the stream or its REST fallback; pass undefined to skip. */
export function usePerpMark(perp: string | undefined): PerpMark {
  const ws = useWs('instrument');
  const info = useInfoClient();
  const feed = useMemo(() => (perp ? perpMarkFeed(ws, info, perp) : null), [ws, info, perp]);
  return useSyncExternalStore(feed?.subscribe ?? noSubscribe, feed?.getSnapshot ?? idleSnapshot);
}
